import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import profileService from "../../services/profileService";
import { toast } from "react-toastify";
import { FaPencilAlt, FaRegTrashAlt, FaEye, FaCamera } from "react-icons/fa";
import { Link } from "react-router-dom";
import { validateEmail, validatePhone } from "../../utils/helpers";

const Profile = () => {
  const [image, setImage] = useState("https://i.pravatar.cc/150?img=3");

  const { user } = useSelector((state) => state.auth);
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    image_url: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [original, setOriginal] = useState(form);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const me = await profileService.getMe();
        if (mounted && me) {
          setForm({
            first_name: me.first_name || "",
            last_name: me.last_name || "",
            email: me.email || "",
            phone: me.phone || "",
            image_url: me.image_url || "",
          });
          setOriginal({
            first_name: me.first_name || "",
            last_name: me.last_name || "",
            email: me.email || "",
            phone: me.phone || "",
            image_url: me.image_url || "",
          });
        }
      } catch (e) {
        // ignore
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const updateField = (e) => {
    const { name, value } = e.target;
    if (name === "phone") {
      // Only allow digits and limit to 10 digits maximum
      const cleaned = value.replace(/\D/g, "").slice(0, 10);
      setForm((prev) => ({ ...prev, [name]: cleaned }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const tempErrors = {};

    const firstName = (form.first_name || "").trim();
    if (!firstName) {
      tempErrors.first_name = "First name is required";
    } else if (firstName.length < 2) {
      tempErrors.first_name = "First name must be at least 2 characters";
    } else if (firstName.length > 50) {
      tempErrors.first_name = "First name cannot exceed 50 characters";
    } else if (!/^[A-Za-z\s]+$/.test(firstName)) {
      tempErrors.first_name = "First name should only contain letters and spaces";
    }

    const lastName = (form.last_name || "").trim();
    if (!lastName) {
      tempErrors.last_name = "Last name is required";
    } else if (lastName.length < 1) {
      tempErrors.last_name = "Last name must be at least 1 character";
    } else if (lastName.length > 50) {
      tempErrors.last_name = "Last name cannot exceed 50 characters";
    } else if (!/^[A-Za-z\s]+$/.test(lastName)) {
      tempErrors.last_name = "Last name should only contain letters and spaces";
    }

    const email = (form.email || "").trim();
    if (!email) {
      tempErrors.email = "Email is required";
    } else if (!validateEmail(email)) {
      tempErrors.email = "Please enter a valid email address";
    }

    const phone = (form.phone || "").trim();
    if (!phone) {
      tempErrors.phone = "Mobile number is required";
    } else if (!/^\d{10}$/.test(phone)) {
      tempErrors.phone = "Mobile number must be exactly 10 digits";
    }

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const onSave = async (e) => {
    if (e) e.preventDefault();
    if (!validateForm()) {
      toast.error("Please correct the errors in the form");
      return;
    }
    setSaving(true);
    try {
      const updated = await profileService.updateMe(form);
      if (updated) {
        setForm((prev) => ({ ...prev, ...updated }));
        // compute changes to show in toast
        const changes = [];
        ["first_name", "last_name", "email", "phone", "image_url"].forEach(
          (key) => {
            if (
              (original?.[key] || "") !== (updated?.[key] || form[key] || "")
            ) {
              changes.push(key.replace("_", " "));
            }
          }
        );
        if (changes.length > 0) {
          toast.success(`Record updated successfully`);
        } else {
          toast.info("No changes detected");
        }
        setOriginal({
          first_name: updated.first_name ?? form.first_name,
          last_name: updated.last_name ?? form.last_name,
          email: updated.email ?? form.email,
          phone: updated.phone ?? form.phone,
          image_url: updated.image_url ?? form.image_url,
        });
        setEditMode(false);
        setErrors({});
      }
    } finally {
      setSaving(false);
    }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!editMode) {
      toast.info("Click Edit to change your photo");
      return;
    }

    // Validate avatar file format and size
    if (!file.type.startsWith("image/")) {
      toast.error("Please select a valid image file");
      return;
    }
    const maxSizeBytes = 2 * 1024 * 1024; // 2MB limit
    if (file.size > maxSizeBytes) {
      toast.error("File size must be less than 2MB");
      return;
    }

    setSaving(true);
    try {
      const updated = await profileService.uploadAvatar(file);
      if (updated) {
        setForm((prev) => ({
          ...prev,
          image_url: updated.image_url || prev.image_url,
        }));
        toast.success("Record updated successfully");
      }
    } finally {
      setSaving(false);
    }
  };

  const readOnly = useMemo(() => !editMode || saving, [editMode, saving]);

  if (loading) return null;
  const defaultImg = process.env.PUBLIC_URL + "/not-img-found.png";

  return (
    <div className="container mt-2">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <Link className="breadcrum" to="/">
          Home{"/"}
          <i
            className="fa-solid fa-chevron-right"
            style={{ marginLeft: "1px", marginRight: "1px" }}
          ></i>{" "}
          Profile
        </Link>
      </div>
      <div className="row">
        {/* <div className="col-md-4 mb-3">
          <div
            className="card p-3 text-center shadow-sm"
            style={{ borderRadius: 16, border: "1px solid #eee" }}
          >
            <div className="d-flex flex-column align-items-center">
              <div
                style={{
                  width: 140,
                  height: 140,
                  borderRadius: "50%",
                  overflow: "hidden",
                  boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
                }}
              >
                <img
                  src={form.image_url || "/avatar-placeholder.png"}
                  alt="avatar"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              </div>

              <div className="w-100 mt-3">
                <input
                  id="file-uplad"
                  type="file"
                  accept="image/*"
                  onChange={onUpload}
                  className="form-control form-control-sm"
                  disabled={readOnly}
                />
              </div>

              {editMode && (
                <button
                  onClick={onSave}
                  className="btn btn-primary btn-sm mt-3"
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Save"}
                </button>
              )}
            </div>
          </div>
        </div> */}

        <div className="col-md-12 mb-3">
          <div
            className="card p-3 bg-light"
            style={{
              borderRadius: "12",
            }}
          >
            <div className="profile-upload">
              <div className="profile-pic">
                <img
                  src={
                    form?.image_url && form.image_url.trim() !== ""
                      ? form.image_url
                      : process.env.PUBLIC_URL + "/not-img-found.png"
                  }
                  alt="Profile"
                  onError={(e) =>
                  (e.target.src =
                    process.env.PUBLIC_URL + "/not-img-found.png")
                  }
                />
                <button
                  type="button"
                  className="upload-icon"
                  style={{
                    borderRadius: "50%",
                    cursor: editMode ? "pointer" : "not-allowed",
                    opacity: editMode ? 1 : 0.5,
                    pointerEvents: editMode ? "auto" : "none",
                  }}
                  onClick={() => document.getElementById("fileInput").click()}
                  disabled={!editMode}
                >
                  <FaCamera />
                </button>
                <input
                  type="file"
                  id="fileInput"
                  accept="image/*"
                  onChange={onUpload}
                />
              </div>

              <div className="profile-info">
                <h4 className="profile-name">
                  {form.first_name} {form.last_name}
                </h4>
                <p className="profile-role">
                  {user?.role ||
                    user?.role_name ||
                    (user?.user_type
                      ? user.user_type
                          .replace(/_/g, " ")
                          .toLowerCase()
                          .replace(/\b\w/g, (char) => char.toUpperCase())
                      : "")}
                </p>
                <p className="profile-location">{form.email}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-12">
          <div className="card p-3" style={{ borderRadius: 12 }}>
            <div
              className="d-flex justify-content-between align-items-center"
              style={{ height: "50px" }}
            >
              <h5 className="m-0">Profile</h5>
              {!editMode ? (
                <button
                  type="button"
                  className="btn btn-outline-warning btn-sm"
                  onClick={() => setEditMode(true)}
                >
                  <FaPencilAlt />
                </button>
              ) : (
                <div>
                  <button
                    className="btn btn-primary btn-sm me-2"
                    onClick={onSave}
                    disabled={saving}
                  >
                    {saving ? "Saving..." : "Update"}
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger btn-sm me-2"
                    onClick={() => {
                      setForm(original);
                      setErrors({});
                      setEditMode(false);
                    }}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
            <form onSubmit={onSave}>
              <div className="row">
                {/* <div className="col-md-6 mb-3"></div> */}
                <div className="col-md-6 mb-3">
                  <label className="form-label">First Name <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    name="first_name"
                    className={`form-control ${errors.first_name ? "is-invalid" : ""}`}
                    value={form.first_name}
                    onChange={updateField}
                    disabled={readOnly}
                  />
                  {errors.first_name && (
                    <div className="invalid-feedback">{errors.first_name}</div>
                  )}
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Last Name <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    name="last_name"
                    className={`form-control ${errors.last_name ? "is-invalid" : ""}`}
                    value={form.last_name}
                    onChange={updateField}
                    disabled={readOnly}
                  />
                  {errors.last_name && (
                    <div className="invalid-feedback">{errors.last_name}</div>
                  )}
                </div>
              </div>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">Email <span className="text-danger">*</span></label>
                  <input
                    type="email"
                    name="email"
                    className={`form-control ${errors.email ? "is-invalid" : ""}`}
                    value={form.email}
                    onChange={updateField}
                    disabled={readOnly}
                  />
                  {errors.email && (
                    <div className="invalid-feedback">{errors.email}</div>
                  )}
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Mobile <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    name="phone"
                    className={`form-control ${errors.phone ? "is-invalid" : ""}`}
                    value={form.phone}
                    onChange={updateField}
                    disabled={readOnly}
                  />
                  {errors.phone && (
                    <div className="invalid-feedback">{errors.phone}</div>
                  )}
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Role</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={
                      user?.user_type
                        ? user.user_type
                          .replace(/_/g, " ") // replace underscores with spaces
                          .toLowerCase() // convert to lowercase first
                          .replace(/\b\w/g, (char) => char.toUpperCase()) // capitalize first letter of each word
                        : ""
                    }
                    disabled
                    readOnly
                  />
                </div>
              </div>
              {/* Buttons are rendered in header for better UX */}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
